import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import createProse from './utils/createProse';

const Prose = React.forwardRef((props, ref) => {
  const {
    className: classNameProp = '',
    children,
    disableMargins = false,
    h1 = { variant: 'h1', weight: 'bold', color: 'primary' },
    h2 = { variant: 'h2', weight: 'bold', color: 'primary' },
    h3 = { variant: 'h3', weight: 'bold', color: 'primary' },
    h4 = { variant: 'h4', weight: 'bold', color: 'primary' },
    h5 = { variant: 'h5', weight: 'bold', color: 'primary' },
    h6 = { variant: 'h6', weight: 'bold', color: 'primary' },
    paragraph = { variant: 'paragraph', weight: 'regular', color: 'primary' },
    ...restProps
  } = props;

  const proseClasses = createProse({
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    paragraph
  });

  const classes = classNames({
    'sui-prose': !disableMargins
  }, proseClasses, classNameProp);

  return (
    <div ref={ref} className={classes} {...restProps}>
      { children }
    </div>
  );
});

Prose.displayName = 'Prose';

Prose.propTypes = {
  /**
   * The dynamic content to be styled
   */
  children: PropTypes.node,
  /**
   * Used to pass classes to the component.
   * @ignore
   */
  className: PropTypes.string,
  /**
   * if true disables the margins in all the children elements.
   * @default false
   */
  disableMargins: PropTypes.bool,
  /**
   * Defines the styling to be applied to the h1 element.
   */
  h1: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['underline']),
    height: PropTypes.oneOf(['none', 'snug']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    uppercase: PropTypes.bool,
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ]),
    weight: PropTypes.oneOf(['bold', 'display'])
  }),
  /**
   * Defines the styling to be applied to the h2 element.
   */
  h2: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['underline']),
    height: PropTypes.oneOf(['none', 'snug']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    uppercase: PropTypes.bool,
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ]),
    weight: PropTypes.oneOf(['bold', 'display'])
  }),
  /**
   * Defines the styling to be applied to the h3 element.
   */
  h3: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['underline']),
    height: PropTypes.oneOf(['none', 'snug']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    uppercase: PropTypes.bool,
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ]),
    weight: PropTypes.oneOf(['bold', 'display'])
  }),
  /**
   * Defines the styling to be applied to the h4 element.
   */
  h4: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['underline']),
    height: PropTypes.oneOf(['none', 'snug']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    uppercase: PropTypes.bool,
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ]),
    weight: PropTypes.oneOf(['bold', 'display'])
  }),
  /**
   * Defines the styling to be applied to the h5 element.
   */
  h5: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['underline']),
    height: PropTypes.oneOf(['none', 'snug']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    uppercase: PropTypes.bool,
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ]),
    weight: PropTypes.oneOf(['bold', 'display'])
  }),
  /**
   * Defines the styling to be applied to the h6 element.
   */
  h6: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['underline']),
    height: PropTypes.oneOf(['none', 'snug']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    uppercase: PropTypes.bool,
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ]),
    weight: PropTypes.oneOf(['bold', 'display'])
  }),
  /**
   * Defines the styling to be applied to the paragraph elements.
   */
  paragraph: PropTypes.shape({
    align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
    color: PropTypes.oneOf([
      'primary',
      'subtle',
      'info',
      'success',
      'warning',
      'danger',
      'brand',
      'inverse',
      'inactive',
      'none'
    ]),
    decoration: PropTypes.oneOf(['line-through', 'underline']),
    height: PropTypes.oneOf(['tight', 'normal', 'loose']),
    lineClamp: PropTypes.oneOfType([
      PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
      PropTypes.shape({
        default: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        sm: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        md: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        lg: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        xl: PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6]),
        '2xl': PropTypes.oneOf(['none', 1, 2, 3, 4, 5, 6])
      })
    ]),
    truncate: PropTypes.bool,
    variant: PropTypes.oneOf([
      'body-base',
      'body-lg',
      'body-xs'
    ]),
    weight: PropTypes.oneOf(['bold', 'regular'])
  }),
};

Prose.defaultProps = {};

export { Prose };
